import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";

function pad(n) { return String(n).padStart(2, "0"); }

export default function QuickBookDialog({ initial, staffList, services, onClose, onCreated }) {
  const [mode, setMode] = useState(initial.openBlockTab ? "block" : "appointment"); // 'appointment' | 'block'
  const [step, setStep] = useState("client");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [client, setClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: "", phone: "", email: "" });
  const [showNewClient, setShowNewClient] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);

  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState(initial.staff_id || "");
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [notes, setNotes] = useState("");
  const [avail, setAvail] = useState({ slots: [], all_slots: [], occupied: [] });
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef();

  // Block form state
  const [blockStaffId, setBlockStaffId] = useState(initial.staff_id || (staffList[0]?.id || ""));
  const [blockStartDate, setBlockStartDate] = useState(initial.date);
  const [blockStartTime, setBlockStartTime] = useState(initial.time);
  const [blockEndDate, setBlockEndDate] = useState(initial.date);
  const [blockEndTime, setBlockEndTime] = useState(() => {
    const [h, m] = (initial.time || "10:00").split(":").map(Number);
    const end = new Date();
    end.setHours(h, m + 60, 0, 0);
    return `${pad(end.getHours())}:${pad(end.getMinutes())}`;
  });
  const [blockAllDay, setBlockAllDay] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  // Search debounced
  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/admin/clients-search", { params: { q } });
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const activeServices = useMemo(() => (services || []).filter((s) => s.active !== false), [services]);
  const staffForService = useMemo(() => {
    if (!serviceId) return staffList;
    return staffList.filter((s) => (s.service_ids || []).includes(serviceId));
  }, [staffList, serviceId]);

  // Load availability when staff+service+date defined
  useEffect(() => {
    if (!staffId || !serviceId || !date) { setAvail({ slots: [], all_slots: [], occupied: [] }); return; }
    (async () => {
      try {
        const r = await api.get(`/staff/${staffId}/availability`, { params: { date, service_id: serviceId } });
        setAvail(r.data || { slots: [], all_slots: [], occupied: [] });
      } catch { setAvail({ slots: [], all_slots: [], occupied: [] }); }
    })();
  }, [staffId, serviceId, date]);

  const canSubmit = client && serviceId && staffId && date && time && !submitting;

  async function createClient() {
    if (!newClient.name.trim()) { toast.error("Nombre requerido"); return; }
    setCreatingClient(true);
    try {
      const r = await api.post("/admin/clients", newClient);
      setClient(r.data);
      setShowNewClient(false);
      setStep("appointment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cliente");
    } finally { setCreatingClient(false); }
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        service_id: serviceId,
        staff_id: staffId,
        date,
        time,
        client_name: client.name,
        client_email: client.email || "",
        client_phone: client.phone || "",
        notes,
      });
      onCreated?.("appointment");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la cita");
    } finally { setSubmitting(false); }
  }

  async function submitBlock() {
    if (!blockStaffId || !blockStartDate || !blockEndDate) { toast.error("Completa profesional y fechas"); return; }
    setBlockSubmitting(true);
    try {
      const payload = {
        staff_id: blockStaffId,
        start_date: blockStartDate,
        end_date: blockEndDate,
        start_time: blockAllDay ? "00:00" : blockStartTime,
        end_time: blockAllDay ? "23:59" : blockEndTime,
        reason: blockReason,
      };
      await api.post("/admin/staff-blocks", payload);
      onCreated?.("block");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el bloqueo");
    } finally { setBlockSubmitting(false); }
  }

  const isFreeSlot = (s) => (avail.slots || []).includes(s);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-book-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "block" ? "Bloquear tiempo" : "Nueva cita"}</DialogTitle>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="appointment" data-testid="qb-tab-appointment">Nueva cita</TabsTrigger>
            <TabsTrigger value="block" data-testid="qb-tab-block">Bloquear tiempo</TabsTrigger>
          </TabsList>

          <TabsContent value="appointment" className="mt-4">
        {step === "client" && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Paso 1 · Cliente</p>
            <div className="space-y-2">
              <Label htmlFor="client-search">Buscar cliente por nombre o teléfono</Label>
              <Input
                id="client-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ej. Ana Pérez o 5551234567"
                data-testid="qb-client-search"
                autoFocus
              />
            </div>
            {searching && <p className="text-xs text-slate-400">Buscando…</p>}
            {results.length > 0 && (
              <div className="border border-slate-200 rounded-md max-h-52 overflow-y-auto" data-testid="qb-client-results">
                {results.map((c) => (
                  <button
                    key={c.id || c.email || c.phone || c.name}
                    onClick={() => { setClient(c); setStep("appointment"); }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0"
                    data-testid={`qb-client-pick-${c.id || c.email || c.phone}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{c.name || "(sin nombre)"}</p>
                      <p className="text-xs text-slate-500">{c.phone || "—"} · {c.email || "sin email"}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!showNewClient ? (
              <div className="flex justify-between items-center pt-2">
                <p className="text-xs text-slate-500">¿No aparece? Crea uno nuevo.</p>
                <Button variant="outline" size="sm" onClick={() => setShowNewClient(true)} data-testid="qb-new-client-open">
                  + Nuevo cliente
                </Button>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-md p-3 space-y-2">
                <p className="text-xs font-medium text-slate-700">Datos del nuevo cliente</p>
                <div className="grid grid-cols-1 gap-2">
                  <Input placeholder="Nombre*" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="qb-new-client-name" />
                  <Input placeholder="Teléfono" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} data-testid="qb-new-client-phone" />
                  <Input placeholder="Email (opcional)" type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} data-testid="qb-new-client-email" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setShowNewClient(false)}>Cancelar</Button>
                  <Button size="sm" onClick={createClient} disabled={creatingClient} data-testid="qb-new-client-save">
                    {creatingClient ? "Guardando…" : "Guardar y usar"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === "appointment" && client && (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500">Cliente</p>
                <p className="text-sm font-medium">{client.name}</p>
                <p className="text-xs text-slate-500">{client.phone || "—"} · {client.email || "sin email"}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setClient(null); setStep("client"); }} data-testid="qb-change-client">Cambiar</Button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>Servicio</Label>
                <Select value={serviceId} onValueChange={(v) => { setServiceId(v); setStaffId(""); }}>
                  <SelectTrigger data-testid="qb-service"><SelectValue placeholder="Selecciona un servicio" /></SelectTrigger>
                  <SelectContent>
                    {activeServices.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.duration_min} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Profesional</Label>
                <Select value={staffId} onValueChange={setStaffId} disabled={!serviceId}>
                  <SelectTrigger data-testid="qb-staff"><SelectValue placeholder={serviceId ? "Selecciona un profesional" : "Elige un servicio primero"} /></SelectTrigger>
                  <SelectContent>
                    {staffForService.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="qb-date" />
                </div>
                <div>
                  <Label>Hora</Label>
                  <Select value={time} onValueChange={setTime} disabled={!staffId || !serviceId}>
                    <SelectTrigger data-testid="qb-time"><SelectValue placeholder="Hora" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {(avail.all_slots || []).map((s) => {
                        const free = isFreeSlot(s);
                        return (
                          <SelectItem key={s} value={s} disabled={!free}>
                            {s}{free ? "" : " · Ocupado"}
                          </SelectItem>
                        );
                      })}
                      {(!avail.all_slots || avail.all_slots.length === 0) && (
                        <SelectItem value="__none__" disabled>Sin horarios disponibles</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Opcional" data-testid="qb-notes" />
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={submit} disabled={!canSubmit} data-testid="qb-submit">
                {submitting ? "Creando…" : "Crear cita"}
              </Button>
            </div>
            {!client.email && (
              <p className="text-xs text-slate-400 text-right">Sin email — no se enviará confirmación al cliente.</p>
            )}
          </div>
        )}
          </TabsContent>

          <TabsContent value="block" className="mt-4">
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Bloquea el tiempo de un profesional (reunión, vacaciones, capacitación, etc). No se creará ninguna cita ni se enviará notificación.</p>
              <div>
                <Label>Profesional</Label>
                <Select value={blockStaffId} onValueChange={setBlockStaffId}>
                  <SelectTrigger data-testid="qb-block-staff"><SelectValue placeholder="Selecciona un profesional" /></SelectTrigger>
                  <SelectContent>
                    {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={blockAllDay}
                  onChange={(e) => setBlockAllDay(e.target.checked)}
                  data-testid="qb-block-allday"
                  className="h-4 w-4 rounded border-slate-300"
                />
                Bloqueo de día(s) completo(s)
              </label>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Desde fecha</Label>
                  <Input type="date" value={blockStartDate} onChange={(e) => setBlockStartDate(e.target.value)} data-testid="qb-block-start-date" />
                </div>
                <div>
                  <Label>Hasta fecha</Label>
                  <Input type="date" value={blockEndDate} onChange={(e) => setBlockEndDate(e.target.value)} data-testid="qb-block-end-date" />
                </div>
              </div>

              {!blockAllDay && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Hora inicio</Label>
                    <Input type="time" value={blockStartTime} onChange={(e) => setBlockStartTime(e.target.value)} data-testid="qb-block-start-time" />
                  </div>
                  <div>
                    <Label>Hora fin</Label>
                    <Input type="time" value={blockEndTime} onChange={(e) => setBlockEndTime(e.target.value)} data-testid="qb-block-end-time" />
                  </div>
                </div>
              )}

              <div>
                <Label>Motivo</Label>
                <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Ej. Reunión, Vacaciones, Cita médica" data-testid="qb-block-reason" />
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                <Button onClick={submitBlock} disabled={blockSubmitting || !blockStaffId} data-testid="qb-block-submit">
                  {blockSubmitting ? "Guardando…" : "Guardar bloqueo"}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
